// src/pages/cashier/CashierDashboard.tsx
import React, { useState } from "react";
import { useCashierStore } from "../../store/cashier.store";
import { useUserStore } from "../../store/user.store";
import { useAuth } from "../../context/AuthProvider";
import CashierNewOrder from "./CashierNewOrder";
import CashierSettlementPage from "./CashierSettlementPage";

type DashboardView = "main" | "new-order" | "settlement";

const CashierDashboard: React.FC = () => {
  const [view, setView] = useState<DashboardView>("main");
  const { pipelineOrders } = useCashierStore();
  const { userProfile } = useUserStore();
  const { user } = useAuth();
  const profile = userProfile || user;

  if (!profile) {
    return <div className="text-center p-8 text-slate-600">Loading cashier profile...</div>;
  }

  // New Order Flow
  if (view === "new-order") {
    return (
      <CashierNewOrder
        onBack={() => {
          setView("main");
        }}
      />
    );
  }

  // Settlement Flow
  if (view === "settlement") {
    return (
      <CashierSettlementPage
        onBack={() => {
          setView("main");
        }}
      />
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            💳 Cashier Dashboard
          </h1>
          <p className="text-slate-300">
            Cashier: <span className="font-semibold">{profile.name}</span> •{" "}
            <span className="font-mono">{profile.phone}</span>
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Pipeline Orders Card */}
          <div className="bg-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-100 mb-1">Active Orders</p>
                <p className="text-3xl font-bold">{pipelineOrders.length}</p>
                <p className="text-xs text-blue-100 mt-2">/ 5 max</p>
              </div>
              <div className="text-5xl opacity-20">📦</div>
            </div>
          </div>

          {/* Quick Actions Stats */}
          <div className="bg-green-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-100 mb-1">Ready to Order</p>
                <p className="text-3xl font-bold">
                  {pipelineOrders.filter((o) => o.items.length > 0).length}
                </p>
                <p className="text-xs text-green-100 mt-2">with items</p>
              </div>
              <div className="text-5xl opacity-20">✅</div>
            </div>
          </div>

          {/* Total Value */}
          <div className="bg-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-100 mb-1">Pipeline Value</p>
                <p className="text-3xl font-bold">
                  ₹
                  {pipelineOrders
                    .reduce((sum, o) => sum + o.totalAmount, 0)
                    .toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-purple-100 mt-2">total pending</p>
              </div>
              <div className="text-5xl opacity-20">💰</div>
            </div>
          </div>
        </div>

        {/* Main Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* New Order Button */}
          <button
            onClick={() => setView("new-order")}
            className="bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg p-8 text-center transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={pipelineOrders.length >= 5}
          >
            <div className="text-5xl mb-3">➕</div>
            <h2 className="text-2xl font-bold mb-2">New Order</h2>
            <p className="text-blue-100">
              {pipelineOrders.length >= 5
                ? "Max 5 orders reached"
                : "Create a new order from any kitchen"}
            </p>
          </button>

          {/* Settlement Button */}
          <button
            onClick={() => setView("settlement")}
            className="bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg p-8 text-center transition-all transform hover:scale-105 shadow-lg"
          >
            <div className="text-5xl mb-3">💳</div>
            <h2 className="text-2xl font-bold mb-2">End-of-Day Settlement</h2>
            <p className="text-green-100">Settle and finalize collections</p>
          </button>
        </div>

        {/* Pipeline Orders Preview */}
        {pipelineOrders.length === 0 ? (
          <div className="bg-slate-700/80 rounded-lg p-8 text-center border border-dashed border-slate-500">
            <p className="text-slate-200 text-lg font-semibold">No active drafts</p>
            <p className="text-slate-400 text-sm mt-2">Tap New Order to start a walk-in / phone order.</p>
          </div>
        ) : (
          <div className="bg-slate-700 rounded-lg p-6 overflow-x-auto">
            <h3 className="text-xl font-bold text-white mb-4">📋 Active Orders</h3>
            <div className="space-y-3">
              {pipelineOrders.map((draft) => (
                <div
                  key={draft.id}
                  className="bg-slate-600 rounded-lg p-4 flex items-center justify-between hover:bg-slate-500 transition-colors"
                >
                  <div>
                    <p className="font-semibold text-white">
                      {draft.customerName || "No name"} •{" "}
                      <span className="text-slate-300">{draft.customerPhone}</span>
                    </p>
                    <p className="text-sm text-slate-300">
                      {draft.items.length} items • {draft.orderType}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-white">
                      ₹{draft.totalAmount}
                    </p>
                    <p className="text-xs text-slate-300">
                      {draft.items.length > 0 ? "Ready" : "Setup"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashierDashboard;
